/**
 * Transcript Block Component
 * Displays conversation transcript with analysis summary
 */

import { Badge } from "@/components/ui/badge";
import { TranscriptBlock as TranscriptBlockType } from "@/types/interview-feedback.types";
import { User, Bot, AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface TranscriptBlockProps {
  block: TranscriptBlockType;
}

export const TranscriptBlock = ({ block }: TranscriptBlockProps) => {
  const { helper, transcript, summary } = block;

  return (
    <div className="space-y-6">
      {/* Helper */}
      {helper && (
        <p className="text-sm text-muted-foreground">{helper}</p>
      )}

      {/* Summary Section */}
      {summary && (
          <div className="grid md:grid-cols-2 gap-4">
            {/* Mistakes */}
            {summary.mistakes && summary.mistakes.sentences.length > 0 && (
              <div className="space-y-3 p-4 rounded-lg bg-muted/20 backdrop-blur-sm border-l-4" style={{ borderColor: summary.mistakes.color }}>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" style={{ color: summary.mistakes.color }} />
                  <h4 className="font-semibold text-foreground">Areas to Improve</h4>
                  <Badge variant="secondary" className="ml-auto">
                    {summary.mistakes.sentences.length}
                  </Badge>
                </div>
                <ul className="space-y-2">
                  {summary.mistakes.sentences.map((sentence, index) => (
                    <li key={index} className="text-sm text-muted-foreground leading-relaxed flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>{sentence}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Corrections */}
            {summary.corrections && summary.corrections.sentences.length > 0 && (
              <div className="space-y-3 p-4 rounded-lg bg-muted/20 backdrop-blur-sm border-l-4" style={{ borderColor: summary.corrections.color }}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" style={{ color: summary.corrections.color }} />
                  <h4 className="font-semibold text-foreground">Well Done</h4>
                  <Badge variant="secondary" className="ml-auto">
                    {summary.corrections.sentences.length}
                  </Badge>
                </div>
                <ul className="space-y-2">
                  {summary.corrections.sentences.map((sentence, index) => (
                    <li key={index} className="text-sm text-muted-foreground leading-relaxed flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>{sentence}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      )}

      {/* Transcript */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-border/50">
          <h4 className="font-semibold text-foreground">Full Conversation</h4>
          <Badge variant="outline" className="ml-auto">
            {transcript.length} messages
          </Badge>
        </div>

        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
          {transcript.map((message, index) => {
            const isUser = message.role === "user";
            
            return (
              <div
                key={index}
                className={cn(
                  "flex gap-3 animate-fade-in",
                  isUser ? "flex-row-reverse" : "flex-row"
                )}
              >
                {/* Avatar */}
                <div
                  className={cn(
                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                    isUser
                      ? "bg-primary/20 text-primary"
                      : "bg-accent/20 text-accent"
                  )}
                >
                  {isUser ? (
                    <User className="w-4 h-4" />
                  ) : (
                    <Bot className="w-4 h-4" />
                  )}
                </div>

                {/* Message Bubble */}
                <div
                  className={cn(
                    "flex-1 max-w-[80%] space-y-1",
                    isUser ? "items-end" : "items-start"
                  )}
                >
                  <div
                    className={cn(
                      "px-4 py-3 rounded-2xl backdrop-blur-sm",
                      isUser
                        ? "bg-primary/10 rounded-tr-sm"
                        : "bg-muted/40 rounded-tl-sm"
                    )}
                  >
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                      {message.content}
                    </p>
                  </div>
                  <div
                    className={cn(
                      "text-xs text-muted-foreground px-2",
                      isUser ? "text-right" : "text-left"
                    )}
                  >
                    {isUser ? "You" : "Host"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
